/**
 * Reading operations service.
 * Provides async functions for manual reading CRUD operations.
 */

import { QueryClient } from "@tanstack/react-query";
import { queryClient as globalQueryClient } from "common/queryClient";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import { Field } from "model";
import { EventType, ManualDMInputResponse } from "model/manualInputTypes";

// Use the same API class pattern as useManualReadingsQueries
const manualInputApi = new API<ManualDMInputResponse>(
  "manualDryMatterInput",
  "v2"
);

/**
 * Prepare a reading request for the backend.
 * Transforms values to backend format (divides by 10000).
 * Transforms values to backend format.
 */
function prepareReadingRequest(
  manualInput: ManualDMInputResponse
): ManualDMInputResponse {
  return {
    guid: manualInput.guid,
    manualDryMatterInput: {
      ...manualInput.manualDryMatterInput,
      dryMatterValue:
        manualInput.manualDryMatterInput.dryMatterValue &&
        manualInput.manualDryMatterInput.dryMatterValue / 10000,
      dateTimeMeasurementStart:
        manualInput.manualDryMatterInput.dateTimeMeasurementStart,
      dateTimeMeasurementEnd:
        manualInput.manualDryMatterInput.dateTimeMeasurementEnd,
      event: {
        ...manualInput.manualDryMatterInput.event,
        postEventCover:
          manualInput.manualDryMatterInput.event.postEventCover &&
          manualInput.manualDryMatterInput.event.postEventCover / 10000,
        preEventCover:
          manualInput.manualDryMatterInput.event.preEventCover &&
          manualInput.manualDryMatterInput.event.preEventCover / 10000,
      },
    },
  };
}

/**
 * Invalidate readings cache for a field so event lists update.
 */
async function invalidateReadingsCache(fieldGuid: string) {
  await globalQueryClient.invalidateQueries({
    queryKey: queryKeys.events.readings.byField(fieldGuid),
  });
}

/**
 * Create a single reading.
 * Transforms values to backend format (divides by 10000).
 */
export async function createOneReading(
  reading: ManualDMInputResponse
): Promise<ManualDMInputResponse> {
  const preparedInput = prepareReadingRequest(reading);
  const result = await manualInputApi.createOne(preparedInput);
  await invalidateReadingsCache(reading.manualDryMatterInput.field);
  return manualInputApi.getOne(result.guid);
}

/**
 * Update a single reading.
 */
export async function updateOneReading(
  reading: ManualDMInputResponse
): Promise<ManualDMInputResponse> {
  await manualInputApi.updateOne(reading);
  await invalidateReadingsCache(reading.manualDryMatterInput.field);
  return manualInputApi.getOne(reading.guid);
}

/**
 * Delete a single reading.
 */
export async function deleteOneReading(
  readingId: string,
  fieldGuid?: string
): Promise<void> {
  await manualInputApi.deleteOne(readingId);
  if (fieldGuid) {
    await invalidateReadingsCache(fieldGuid);
  }
}

/**
 * Move animals into a field.
 * Creates a reading with move-in event and updates field states.
 */
export async function moveIntoField(
  newFieldGuid: string,
  currentFieldGuid: string | undefined,
  dateTimeIn: Date,
  queryClient: QueryClient
): Promise<void> {
  // Create a move-in event reading
  // Creates a move-in event reading
  const moveInEvent: ManualDMInputResponse = {
    guid: "new",
    manualDryMatterInput: {
      field: newFieldGuid,
      dateTimeMeasurementStart: dateTimeIn.toISOString(),
      dateTimeMeasurementEnd: dateTimeIn.toISOString(),
      event: {
        eventType: EventType.GrazingEvent,
        dateTimeIn: dateTimeIn.toISOString(),
      },
      kind: "user",
      verificationStatus: "Accepted",
    },
  };

  await createOneReading(moveInEvent);

  // Optimistically update fields (re-fetch since field may have been updated)
  const updatedNewField = queryClient.getQueryData<Field>(
    queryKeys.fields.detail(newFieldGuid)
  );

  if (currentFieldGuid) {
    const currentField = queryClient.getQueryData<Field>(
      queryKeys.fields.detail(currentFieldGuid)
    );

    if (
      updatedNewField &&
      currentField &&
      updatedNewField.animalGroup &&
      updatedNewField.animalGroup === currentField.animalGroup &&
      currentField.animalGroupNow === currentField.animalGroup
    ) {
      // Update new field: set animalGroupNow
      queryClient.setQueryData<Field>(queryKeys.fields.detail(newFieldGuid), {
        ...updatedNewField,
        animalGroupNow: updatedNewField.animalGroup,
      });

      // Update current field: clear animalGroupNow
      queryClient.setQueryData<Field>(
        queryKeys.fields.detail(currentFieldGuid),
        {
          ...currentField,
          animalGroupNow: undefined,
        }
      );
    }
  } else if (updatedNewField) {
    // No current field, just set animalGroupNow on new field
    queryClient.setQueryData<Field>(queryKeys.fields.detail(newFieldGuid), {
      ...updatedNewField,
      animalGroupNow: updatedNewField.animalGroup,
    });
  }

  // Invalidate field queries so they will be refetched after reading creation
  const fieldsToRefetch = [newFieldGuid];
  if (currentFieldGuid) {
    fieldsToRefetch.push(currentFieldGuid);
  }

  await Promise.all(
    fieldsToRefetch.map((fieldGuid) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.fields.detail(fieldGuid),
      })
    )
  );

  await queryClient.invalidateQueries({ queryKey: queryKeys.fields.all });

  // Invalidate readings/events queries so event lists update
  await Promise.all(
    fieldsToRefetch.map((fieldGuid) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.readings.byField(fieldGuid),
      })
    )
  );
}
